/**
 * Agrupación del menú Comunicación (menú 14) en el sidebar del admin.
 *
 * En BD el menú tiene un submenú por vista (Administrar Avisos, Enviar Avisos,
 * Ejecuciones...). Aquí, solo en código, esas vistas se juntan en pocas
 * entradas del sidebar ("Avisos", "Citas", "Chat", "Eventos") y dentro de cada
 * una se cambia de vista con pestañas.
 *
 * Cada pestaña SIGUE SIENDO SU PROPIA RUTA y su propio submenú: de
 * `vista_front_end` dependen la RLS, funciones SQL, gates de Edge Functions y
 * los links de los correos. Nada de esto cambia la BD ni los permisos; solo
 * cómo se pintan.
 *
 * Para revertir: `AGRUPAR_COMUNICACION = false`. El sidebar vuelve a las 9
 * entradas de BD y el layout de pestañas deja pasar la página tal cual.
 */
import type { LucideIcon } from 'lucide-react';
import { CalendarDays, Megaphone, MessagesSquare, Ticket } from 'lucide-react';

/** Interruptor único de la agrupación. */
export const AGRUPAR_COMUNICACION = true;

/** Menú Comunicación, por id (nunca por nombre). */
export const COMUNICACION_MENU_ID = 14;

export interface PestanaComunicacion {
  ruta: string;
  etiqueta: string;
}

export interface GrupoComunicacion {
  id: string;
  /** Nombre en el sidebar y título dentro de la pantalla. */
  titulo: string;
  icono: LucideIcon;
  /** En orden de pestaña. La primera permitida es a donde apunta el sidebar. */
  pestanas: readonly PestanaComunicacion[];
}

export const GRUPOS_COMUNICACION: readonly GrupoComunicacion[] = [
  {
    id: 'avisos',
    titulo: 'Avisos',
    icono: Megaphone,
    pestanas: [
      { ruta: '/admin/comunicacion/administrar-avisos', etiqueta: 'Catálogo' },
      { ruta: '/admin/comunicacion/enviar-avisos', etiqueta: 'Enviar' },
      { ruta: '/admin/comunicacion/ejecuciones', etiqueta: 'Historial' },
      { ruta: '/admin/comunicacion/notificaciones-apps', etiqueta: 'App' },
      { ruta: '/admin/comunicacion/correos-sistema', etiqueta: 'Correos del sistema' },
    ],
  },
  {
    id: 'citas',
    titulo: 'Citas',
    icono: CalendarDays,
    pestanas: [
      { ruta: '/admin/comunicacion/todas-las-citas', etiqueta: 'Agenda' },
      { ruta: '/admin/comunicacion/configuracion-citas', etiqueta: 'Disponibilidad' },
    ],
  },
  {
    id: 'chat',
    titulo: 'Chat',
    icono: MessagesSquare,
    pestanas: [{ ruta: '/admin/comunicacion/chat', etiqueta: 'Chat' }],
  },
  {
    id: 'eventos',
    titulo: 'Eventos',
    icono: Ticket,
    pestanas: [{ ruta: '/admin/comunicacion/eventos', etiqueta: 'Eventos' }],
  },
];

function cubre(ruta: string, path: string): boolean {
  return path === ruta || path.startsWith(`${ruta}/`);
}

/** Grupo al que pertenece la ruta (o una subruta suya), o null. */
export function grupoDeRuta(
  path: string,
  grupos: readonly GrupoComunicacion[] = GRUPOS_COMUNICACION,
): GrupoComunicacion | null {
  return grupos.find((g) => g.pestanas.some((p) => cubre(p.ruta, path))) ?? null;
}

/** Pestaña del grupo que corresponde a la ruta actual, o null. */
export function pestanaDeRuta(grupo: GrupoComunicacion, path: string): PestanaComunicacion | null {
  return grupo.pestanas.find((p) => cubre(p.ruta, path)) ?? null;
}

/**
 * Pestañas que esta sesión puede ver, en orden de pestaña. `permitidas` son las
 * rutas de los submenús que ya pinta el sidebar (mismo filtro de permisos).
 */
export function pestanasPermitidas(
  grupo: GrupoComunicacion,
  permitidas: ReadonlySet<string>,
): PestanaComunicacion[] {
  return grupo.pestanas.filter((p) => permitidas.has(p.ruta));
}

/** Lo que la agrupación necesita de `DynamicMenuChild`. */
export interface HijoMenu<I> {
  title: string;
  href: string;
  icon: I;
  submenuId: number;
  disabled?: boolean;
  /** Rutas con las que la entrada se marca activa (todas las pestañas permitidas). */
  rutasActivas?: string[];
}

/**
 * Junta los submenús de Comunicación en una entrada por grupo. Cada entrada
 * apunta a la primera pestaña permitida, se coloca donde aparecía el primer
 * submenú del grupo y se marca activa en cualquiera de sus rutas. Un submenú
 * que no está en ningún grupo (uno nuevo dado de alta en BD) queda tal cual.
 * Un grupo sin pestañas permitidas no aparece.
 */
export function agruparHijosMenu<I>(
  menuId: number,
  hijos: readonly HijoMenu<I>[],
  opciones: { activo?: boolean; grupos?: readonly GrupoComunicacion[] } = {},
): HijoMenu<I | LucideIcon>[] {
  const { activo = AGRUPAR_COMUNICACION, grupos = GRUPOS_COMUNICACION } = opciones;
  if (!activo || menuId !== COMUNICACION_MENU_ID) return [...hijos];

  const permitidas = new Set(hijos.map((h) => h.href));
  const yaPuestos = new Set<string>();
  const salida: HijoMenu<I | LucideIcon>[] = [];

  for (const hijo of hijos) {
    const grupo = grupoDeRuta(hijo.href, grupos);
    if (!grupo) {
      salida.push(hijo);
      continue;
    }
    if (yaPuestos.has(grupo.id)) continue;
    yaPuestos.add(grupo.id);
    const visibles = pestanasPermitidas(grupo, permitidas);
    const primera = visibles[0];
    if (!primera) continue;
    const base = hijos.find((h) => h.href === primera.ruta) ?? hijo;
    salida.push({
      title: grupo.titulo,
      href: primera.ruta,
      icon: grupo.icono,
      submenuId: base.submenuId,
      disabled: base.disabled,
      rutasActivas: visibles.map((p) => p.ruta),
    });
  }
  return salida;
}

/**
 * ¿Se resalta esta entrada del sidebar? Match exacto o subruta, salvo que un
 * hermano más específico también la cubra (Inventarios → Precios). Una entrada
 * agrupada se resalta en cualquiera de sus `rutasActivas`.
 */
export function esHijoActivo<I>(
  hijo: HijoMenu<I>,
  currentPath: string,
  hermanos: readonly HijoMenu<I>[],
): boolean {
  if (hijo.rutasActivas?.some((r) => cubre(r, currentPath))) return true;
  if (currentPath === hijo.href) return true;
  if (!currentPath.startsWith(`${hijo.href}/`)) return false;
  return !hermanos.some(
    (otro) => otro.href.length > hijo.href.length && cubre(otro.href, currentPath),
  );
}
